/**
 * <DisciplineLandingPage> — one templated landing page that renders any
 * discipline by slug. Loads structured copy from `/data/disciplines.js`.
 *
 * Route table is generated from the `DISCIPLINES` registry — so adding a
 * new discipline is one entry in the data file, zero new components.
 *
 * Acquisition-attribution: the Reserve-Pro modal is given the discipline
 * slug; reservations land in `pro_reservations` with the discipline tag,
 * surfaced in the admin Pricing & Tiers panel `by_discipline` rollup.
 */
import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowRight, CheckCircle2, Sparkles, ShieldCheck, BadgeCheck,
  Wallet, Lock, Stethoscope, Info, MapPin, Clock,
} from 'lucide-react';
import { PublicNavbar } from '../components/brand/PublicNavbar';
import { Footer } from '../components/brand/Footer';
import { Seo } from '../components/seo/Seo';
import { breadcrumbSchema, organizationSchema } from '../lib/seoSchemas';
import { MarketplaceDisclaimer } from '../components/brand/MarketplaceDisclaimer';
import { ClinicianReviewBadge } from '../components/brand/ClinicianReviewBadge';
import { CareLibraryShelf } from '../components/brand/CareLibraryShelf';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../components/ui/accordion';
import { DISCIPLINE_BY_SLUG, DISCIPLINES } from '../data/disciplines';

// Per-discipline topic shortlist for the "Free care guides" shelf. Pulls
// from the caregiver library (richer inventory; clinicians share with the
// families they visit) and biases toward the topics most relevant to the
// discipline's daily caseload. Unknown disciplines fall back to a broad
// home-care topic set so the shelf is always populated.
const DISCIPLINE_CARE_TOPICS = {
  'physical-therapists':         ['safe-physical-care', 'home-safety'],
  'occupational-therapists':     ['home-safety', 'safe-physical-care'],
  'speech-language-pathologists':['nutrition', 'safe-physical-care'],
  'registered-nurses':           ['medication-management', 'safe-physical-care'],
  'psychiatric-mental-health-nps':['caregiver-self-care', 'dementia'],
  'clinical-social-workers':     ['care-navigation', 'caregiver-self-care'],
  'home-health-aides':           ['safe-physical-care', 'home-safety'],
  'family-nurse-practitioners':  ['medication-management', 'care-navigation'],
  'lactation-consultants':       ['nutrition', 'orientation'],
  'wound-care-nurses':           ['safe-physical-care', 'home-safety'],
  'pediatric-therapists':        ['orientation', 'nutrition'],
  'registered-dietitians':       ['nutrition', 'medication-management'],
  'doulas':                      ['orientation', 'caregiver-self-care'],
  'dementia-care-specialists':   ['dementia', 'caregiver-self-care'],
  'chronic-care-telehealth-nps': ['medication-management', 'care-navigation'],
  'medical-massage-therapists':  ['safe-physical-care', 'caregiver-self-care'],
  'audiologists':                ['home-safety', 'care-navigation'],
};
const DEFAULT_CARE_TOPICS = ['safe-physical-care', 'home-safety', 'care-navigation'];

export const DisciplineLandingPage = ({ slug: slugProp }) => {
  // Route can pass slug as a prop (when using explicit routes) or via params.
  const params = useParams();
  const slug = slugProp || params.slug;
  const d = DISCIPLINE_BY_SLUG[slug];

  // Unknown slug → friendly 404 (still SSR-friendly).
  if (!d) {
    return (
      <div className="min-h-screen bg-cn-bone-50 dark:bg-cn-ink-900">
        <PublicNavbar />
        <main className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="discipline-not-found">
          <Seo title="Not found — CaraLoom" description="That discipline page doesn't exist yet." noindex />
          <h1 className="cn-display text-4xl text-cn-text dark:text-white">That discipline page isn’t live yet.</h1>
          <p className="mt-4 text-cn-text-muted">We&rsquo;re rolling out one discipline at a time. Here&rsquo;s what&rsquo;s available now:</p>
          <ul className="mt-6 flex flex-wrap gap-2 justify-center">
            {DISCIPLINES.map((it) => (
              <li key={it.slug}>
                <Link to={`/for-${it.slug}`} className="cn-btn cn-btn-ghost !py-2 !px-3 text-sm">
                  {it.short_label}
                </Link>
              </li>
            ))}
          </ul>
        </main>
        <Footer />
      </div>
    );
  }

  const breadcrumbs = breadcrumbSchema([
    { name: 'CaraLoom', url: '/' },
    { name: 'For Clinicians', url: '/for-clinicians' },
    { name: d.name, url: `/for-${d.slug}` },
  ]);

  return (
    <div className="min-h-screen bg-cn-bone-50 dark:bg-cn-ink-900" data-testid={`discipline-page-${d.slug}`}>
      <Seo
        title={d.seo.title}
        description={d.seo.description}
        path={`/for-${d.slug}`}
        keywords={d.seo.keywords}
        schema={[organizationSchema, breadcrumbs]}
        noindex
      />
      <PublicNavbar />

      {/* ────────────────────────────────────────────── Hero ────────────── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-cn-bone-100 to-cn-bone-50 dark:from-cn-ink-900 dark:to-cn-ink-800" />
        <div className="cn-container relative pt-16 pb-14 md:pt-24 md:pb-20">
          <div className="grid lg:grid-cols-[1.2fr_1fr] gap-10 items-center">
            <div>
              <div className="cn-eyebrow mb-3" data-testid="discipline-eyebrow">For {d.name} · {d.setting}</div>
              <h1
                className="cn-display text-4xl sm:text-5xl lg:text-6xl text-cn-text dark:text-white leading-tight"
                data-testid="discipline-headline"
              >
                {d.headline}
              </h1>
              <p className="mt-6 text-base sm:text-lg text-cn-text-muted leading-relaxed max-w-2xl">{d.pitch}</p>
              {d.pitch_signoff && (
                <p className="mt-3 text-base font-semibold text-cn-primary max-w-2xl" data-testid="discipline-signoff">{d.pitch_signoff}</p>
              )}
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/auth?role=clinician"
                  className="cn-btn cn-btn-primary"
                  data-testid="discipline-hero-signup-btn"
                >
                  <Sparkles className="w-4 h-4" />
                  Start free — flat 12% take
                </Link>
                <Link to="/pricing" className="cn-btn cn-btn-ghost" data-testid="discipline-hero-pricing-link">
                  See full pricing
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-xs text-cn-text-muted">
                <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-cn-primary" /> Verified-settlement payouts</span>
                <span className="inline-flex items-center gap-1.5"><Lock className="w-3.5 h-3.5 text-cn-primary" /> HIPAA-ready (Vertex AI BAA)</span>
                <span className="inline-flex items-center gap-1.5"><BadgeCheck className="w-3.5 h-3.5 text-cn-primary" /> Identity + license verified</span>
                <ClinicianReviewBadge review={d.advisor_review} disciplineName={d.name} />
              </div>
            </div>

            {/* Hero visual — economics callout */}
            <div className="cn-card p-6 lg:p-7 bg-white dark:bg-cn-ink-800" data-testid="discipline-hero-economics">
              <div className="cn-eyebrow">Transparent take</div>
              <h3 className="cn-display text-2xl mt-2">Flat 12 %</h3>
              <p className="text-sm text-cn-text-muted mt-1">Keep 88% of every booking. No agency markup. No insurance billing overhead unless you opt-in. No chasing checks.</p>
              <div className="mt-5 divide-y divide-cn-border">
                {[
                  { row: 'Service rate per booking', value: '12 % flat' },
                  { row: 'You keep', value: '88 %' },
                  { row: 'Verified settlement', value: '1–2 days' },
                  { row: 'Instant payout (optional)', value: '+1.5 % fee' },
                ].map((r) => (
                  <div key={r.row} className="py-3 flex items-center justify-between">
                    <span className="text-sm text-cn-text-muted">{r.row}</span>
                    <span className="text-sm font-semibold text-cn-text dark:text-white">{r.value}</span>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-[11px] text-cn-text-muted">
                Service rate covers payment processing, insurance, credential verification, AI matching, scheduling, and support.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ────────────────────────────────────────────── Why it pays ─────── */}
      <section className="py-16 md:py-20 bg-white dark:bg-cn-ink-800 border-y border-cn-border" data-testid="discipline-why-pays">
        <div className="cn-container">
          <div className="cn-eyebrow mb-3">Why it pays</div>
          <h2 className="cn-display text-3xl sm:text-4xl mb-10">The economics, plainly.</h2>
          <div className="grid md:grid-cols-3 gap-5">
            {d.why_pays.map((row, i) => (
              <div key={i} className="cn-card p-6 bg-cn-bone-50 dark:bg-cn-ink-900 border-cn-border" data-testid={`why-pays-${i}`}>
                <h3 className="font-display font-bold text-cn-text dark:text-white text-lg">{row.label}</h3>
                <p className="mt-2 text-sm text-cn-text-muted leading-relaxed">{row.body}</p>
                {row.illustrative && (
                  <p className="mt-3 text-[11px] uppercase tracking-wider font-bold text-cn-text-muted/70">Illustrative range</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ────────────────────────────────────────────── Free care guides ─ */}
      <CareLibraryShelf
        audience="caregiver"
        topics={DISCIPLINE_CARE_TOPICS[d.slug] || DEFAULT_CARE_TOPICS}
        limit={3}
        title={`Free care guides · ${d.short_label}`}
        subtitle="Verified, public-domain guides you can share with the families you visit."
      />

      {/* ────────────────────────────────────────────── Pain → Solved ───── */}
      <section className="py-16 md:py-20" data-testid="discipline-pain-solved">
        <div className="cn-container">
          <div className="cn-eyebrow mb-3">Pain → Solved</div>
          <h2 className="cn-display text-3xl sm:text-4xl mb-10 max-w-2xl">
            What we&rsquo;ve heard from {d.persona}. And what we&rsquo;re doing about it.
          </h2>
          <div className="rounded-3xl overflow-hidden border border-cn-border bg-white dark:bg-cn-ink-800">
            <div className="grid grid-cols-1 md:grid-cols-2 bg-cn-primary text-white text-sm font-bold uppercase tracking-wider">
              <div className="px-5 py-3">Pain</div>
              <div className="px-5 py-3 border-t md:border-t-0 md:border-l border-white/15">How CaraLoom solves it</div>
            </div>
            {d.pain_table.map((row, i) => (
              <div
                key={i}
                className={`grid grid-cols-1 md:grid-cols-2 border-t border-cn-border ${i % 2 === 0 ? 'bg-cn-bone-50/40 dark:bg-cn-ink-900/30' : ''}`}
                data-testid={`pain-row-${i}`}
              >
                <div className="px-5 py-4 text-sm text-cn-text dark:text-white font-medium">{row.pain}</div>
                <div className="px-5 py-4 text-sm text-cn-text-muted border-t md:border-t-0 md:border-l border-cn-border flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-cn-primary flex-shrink-0 mt-0.5" />
                  <span>{row.solved}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ────────────────────────────────────────────── Honest FAQ ──────── */}
      <section className="py-16 md:py-20 bg-white dark:bg-cn-ink-800 border-y border-cn-border" data-testid="discipline-faqs">
        <div className="cn-container max-w-3xl">
          <div className="cn-eyebrow mb-3">Honest answers</div>
          <h2 className="cn-display text-3xl sm:text-4xl mb-3">The questions a skeptical clinician asks first.</h2>
          <p className="text-cn-text-muted mb-8 text-sm">No marketing fluff. If CaraLoom doesn&rsquo;t solve something, we say so.</p>
          <Accordion type="single" collapsible className="space-y-3">
            {d.faqs.map((qa, i) => (
              <AccordionItem
                key={i}
                value={`q-${i}`}
                className="rounded-2xl border border-cn-border bg-cn-bone-50 dark:bg-cn-ink-900 px-4"
                data-testid={`discipline-faq-${i}`}
              >
                <AccordionTrigger className="text-left font-semibold text-cn-text dark:text-white hover:no-underline py-4">
                  {qa.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-cn-text-muted leading-relaxed pb-4">
                  {qa.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ────────────────────────────────────────────── Final CTA ───────── */}
      <section className="py-16 md:py-20" data-testid="discipline-final-cta">
        <div className="cn-container">
          <div className="cn-card p-8 md:p-12 text-center bg-gradient-to-br from-cn-primary to-cn-primary/85 text-white border-0">
            <Stethoscope className="w-10 h-10 mx-auto mb-4 opacity-80" />
            <h2 className="cn-display text-3xl sm:text-4xl text-white">Start your independent practice in 60 seconds.</h2>
            <p className="mt-3 text-white/80 max-w-xl mx-auto">
              Flat 12% service rate &mdash; keep 88% of every booking, paid in 1&ndash;2 days.
              No tier upgrades. No surprise fees. No credit card to start.
            </p>
            <Link
              to="/auth?role=clinician"
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-cn-primary font-semibold hover:bg-cn-bone-100 transition-colors"
              data-testid="discipline-final-signup-btn"
            >
              <Sparkles className="w-4 h-4" />
              Start free
            </Link>
            <p className="mt-4 text-xs text-white/60 max-w-xl mx-auto">
              CaraLoom is a software marketplace — not a bank, escrow agent, or money transmitter. Funds are secured up-front through Stripe Connect and settle to the clinician once the visit is documented and verified.
            </p>
          </div>
        </div>
      </section>

      <MarketplaceDisclaimer variant="footer" />
      <Footer />
    </div>
  );
};

export default DisciplineLandingPage;
